import React, { useEffect, useState } from 'react';
import { fetchWithCsrf } from '../../utils/fetchWithCsrf';
import loginBackground from '../../../assets/images/login-background';
import LoginFooter from '../../components/layout/Footer/LoginFooter';
import logo from '../../../assets/images/rio-claro-logo-colorida.svg';
import CustomInput from '../../components/ui/inputs/CustomInput';
import CustomPasswordInput from '../../components/ui/inputs/CustomPasswordInput';
import CustomCheckbox from '../../components/ui/checkboxes/CustomCheckbox';
import CustomButton from '../../components/ui/buttons/CustomButton';
import { useToast } from '../../components/ui/toast/ToastContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpAttempt, setOtpAttempt] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberLogin, setRememberLogin] = useState(!!localStorage.getItem('rememberedEmail'));
  const { addToast } = useToast();

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberLogin(true);
    }
  }, []);

  useEffect(() => {
    if (requires2FA && otpAttempt.length === 6 && !loading) {
      handleSubmit(new Event('submit'));
    }
  }, [otpAttempt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (rememberLogin) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    try {
      const res = await fetchWithCsrf('/admins/sign_in', {
        method: 'POST',
        body: JSON.stringify({
          admin: {
            email,
            password,
            ...(requires2FA && { otp_attempt: otpAttempt }),
          },
        }),
        credentials: 'same-origin',
      });

      if (res.ok) {
        addToast({ type: 'success', message: 'Login realizado com sucesso!' });
        window.location.href = '/app/planner/prospects';
      } else {
        const body = await res.json();
        if (body.two_factor_required) {
          setRequires2FA(true);
          addToast({ type: 'info', message: 'Autenticação em duas etapas necessária. Digite o código.' });
        } else {
          addToast({ type: 'error', message: body.error || 'Credenciais inválidas.' });
        }
      }
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', message: 'Erro ao tentar logar.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      <div className="bg-white px-20 py-10 rounded-custom20 w-full max-w-2xl shadow-xl text-blueDarker font-jost">
        <div className="flex items-center justify-center text-center mb-8">
          <img src={logo} alt="Rio Claro Investimentos" className="w-custom240" />
        </div>

        <div className="mb-8">
          <p className="text-2xl font-semibold">Olá, seja bem-vindo!</p>
          <p className="text-base font-normal pt-2">
            {requires2FA
              ? 'Digite o código gerado pelo seu aplicativo autenticador.'
              : 'Para entrar, preencha os campos abaixo com as suas credenciais.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!requires2FA ? (
            <>
              <CustomInput
                disabled={loading}
                type="email"
                label="Endereço de e-mail"
                labelClassName="text-base"
                inputClassName="mb-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <CustomPasswordInput
                disabled={loading}
                name="password"
                label="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                labelClassName="text-base"
              />

              <div className="flex justify-between items-center text-base">
                <CustomCheckbox
                  disabled={loading}
                  name="remember"
                  label="Lembrar meu login"
                  checked={rememberLogin}
                  onChange={() => setRememberLogin(!rememberLogin)}
                  labelClassName="text-base font-normal"
                />
                <a href="/admins/password/new" className="hover:opacity-80 underline font-semibold">
                  Esqueceu a senha?
                </a>
              </div>
            </>
          ) : (
            <CustomInput
              disabled={loading}
              type="text"
              label="Código de verificação (6 dígitos)"
              labelClassName="text-base"
              inputClassName="mb-2"
              value={otpAttempt}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                if (val.length <= 6) setOtpAttempt(val);
              }}
              maxLength={6}
              autoFocus
              required
            />
          )}

          <div className="flex items-center justify-center text-center">
            <CustomButton disabled={loading} type="submit" className="px-14 mt-3 mb-8">
              {requires2FA ? 'VALIDAR CÓDIGO' : 'ENTRAR'}
            </CustomButton>
          </div>
        </form>

        {!requires2FA && (
          <div className="flex items-center justify-center text-center mb-5">
            <span>
              Não possui credenciais?{' '}
              <a href="mailto:ola@humanoide.co" className="font-semibold hover:opacity-80 underline">
                Entre em contato conosco
              </a>
              .
            </span>
          </div>
        )}
      </div>
      <LoginFooter />
    </div>
  );
};

export default Login;
